/**
 * Settings › Users & roles › Roles — the Activity table (`docs/activity.md`
 * §12.1, Board 17 A).
 *
 * *Activity — whose calendars, tasks and conversations each role sees*: a row
 * per role with **Calendars, tasks, notes** and **Inboxes — texts & email**
 * (*Only their own · Their branch · Everyone*, the Inbox adding *None*),
 * **Book & reassign**, and who holds it. Each change saves at once and is
 * audited on the server; the rule that reads them is `activity_people()`.
 * The order is the server's (widest reach first) and holds still while the
 * page is open, so a row never moves out from under the menu that changed it.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { firstNameOf } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { request } from '../lib/api.js'
import { Segmented, Switch } from '../components/primitives.js'
import { Popover } from '../components/overlays.js'
import { Glyph, type GlyphName } from '../activity/glyphs.js'

type CalendarScope = 'own' | 'branch' | 'all'
type InboxScope = 'none' | CalendarScope

export interface RoleActivityRow {
  id: string
  name: string
  calendarScope: CalendarScope
  inboxScope: InboxScope
  bookOthers: boolean
  defaultApp: 'office' | 'crew_app'
  people: string[]
}

interface ScopeOption<T extends string> { value: T; label: string; sub: string }

export const CALENDAR_SCOPE_OPTIONS: ScopeOption<CalendarScope>[] = [
  { value: 'own', label: 'Only their own', sub: 'what they’re on, made or were given' },
  { value: 'branch', label: 'Their branch', sub: 'everyone in the branches they belong to' },
  { value: 'all', label: 'Everyone', sub: 'every branch in the company' },
]

const INBOX_SCOPE_OPTIONS: ScopeOption<InboxScope>[] = [
  ...CALENDAR_SCOPE_OPTIONS,
  { value: 'none', label: 'None', sub: 'no Inbox' },
]

/**
 * *Rachel Stone* · *Alex Kerr, Dan Moyer* · *Tom, Hannah, Luis, Maya, Sara,
 * Derek* · *21 people*: a few people by name, more by first name, a crew by
 * how many — a crew role has no Inbox and its people are not who anybody is
 * looking for here.
 */
export function rolePeopleWords(row: Pick<RoleActivityRow, 'people' | 'defaultApp'>): string {
  const count = row.people.length
  if (row.defaultApp === 'crew_app' || count > 8) return `${count} ${count === 1 ? 'person' : 'people'}`
  if (count === 0) return 'Nobody yet'
  if (count <= 3) return row.people.join(', ')
  return row.people.map(firstNameOf).join(', ')
}

const GRID = 'grid grid-cols-[200px_200px_200px_160px_minmax(0,1fr)] items-center px-4'

/** The 170 × 32 trigger and its 300 px menu of three (or four) — Board 17 A. */
function ScopeSelect<T extends string>({
  value, options, onChange, label, disabled, onOpenChange,
}: {
  value: T
  options: ScopeOption<T>[]
  onChange: (value: T) => void
  label: string
  disabled?: boolean
  onOpenChange?: (open: boolean) => void
}) {
  const trigger = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const chosen = options.find((option) => option.value === value) ?? options[0]!
  const toggle = (next: boolean) => { setOpen(next); onOpenChange?.(next) }
  return (
    <>
      <button
        ref={trigger}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => toggle(!open)}
        className={cn('flex h-8 w-[170px] items-center gap-1.5 rounded-button border bg-surface px-2.5',
          'text-[12.5px] font-semibold disabled:opacity-60',
          open ? 'border-accent shadow-[0_0_0_3px_rgb(var(--color-accent-ring))]' : 'border-line hover:border-line-strong',
          value === 'all' ? 'text-accent-dark' : value === 'none' ? 'text-text-faint' : 'text-text')}
      >
        <span className="min-w-0 flex-1 truncate text-left">{chosen.label}</span>
        <Glyph name="chevronDown" size={13} className="text-text-muted" />
      </button>
      {open && (
        <Popover open onClose={() => toggle(false)} anchor={trigger.current} width={300} label={label}
          className="rounded-[12px] py-1">
          <div role="menu" aria-label={label} className="flex flex-col">
            {options.map((option) => {
              const on = option.value === value
              return (
                <button
                  key={option.value}
                  type="button"
                  role="menuitemradio"
                  aria-checked={on}
                  onClick={() => { toggle(false); if (!on) onChange(option.value) }}
                  className={cn('mx-1 flex items-center gap-2.5 rounded-[9px] px-3 py-[9px] text-left',
                    on ? 'bg-green-band-bg' : 'hover:bg-hover')}
                >
                  <span className="flex min-w-0 flex-1 flex-col">
                    <b className={cn('text-[13px]', on ? 'text-accent-dark' : 'text-text')}>{option.label}</b>
                    <span className="text-[11.5px] text-text-muted">{option.sub}</span>
                  </span>
                  {on && <Glyph name="check" size={15} className="text-accent" />}
                </button>
              )
            })}
          </div>
        </Popover>
      )}
    </>
  )
}

function HeadCell({ glyph, children }: { glyph: GlyphName; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-[5px]">
      <Glyph name={glyph} size={13} />{children}
    </div>
  )
}

export function RolesActivityTable({ canEdit }: { canEdit: boolean }) {
  const navigate = useNavigate()
  const [rows, setRows] = useState<RoleActivityRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [openRow, setOpenRow] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    request<{ items: RoleActivityRow[] }>('/roles/activity')
      .then((answer) => { if (live) setRows(answer.items) })
      .catch((problem: { message?: string }) => { if (live) setError(problem.message ?? 'The roles did not load.') })
    return () => { live = false }
  }, [])

  /* Saved at once (§12.1); the row shows the new value first and goes back if the server refuses. */
  const save = useCallback(async (row: RoleActivityRow, patch: Partial<Pick<RoleActivityRow,
    'calendarScope' | 'inboxScope' | 'bookOthers'>>) => {
    setError(null)
    setRows((all) => all?.map((one) => (one.id === row.id ? { ...one, ...patch } : one)) ?? all)
    try {
      await request(`/roles/${row.id}/activity`, { method: 'PATCH', body: patch })
    } catch (problem) {
      setRows((all) => all?.map((one) => (one.id === row.id ? row : one)) ?? all)
      setError((problem as { message?: string }).message ?? 'That did not save.')
    }
  }, [])

  return (
    <section aria-label="Activity — whose calendars, tasks and conversations each role sees" className="flex flex-col">
      <div className="mb-3.5 flex items-center gap-2.5">
        <Segmented
          label="Users or roles"
          size="large"
          value="roles"
          onChange={(value) => { if (value === 'users') navigate('/settings/people') }}
          options={[{ value: 'users', label: 'Users' }, { value: 'roles', label: 'Roles' }]}
        />
        <span className="flex-1" />
        <span className="text-[12px] text-text-muted">Activity — whose calendars, tasks and conversations each role sees</span>
      </div>

      {/*
        * The four fixed columns and room for a few names: Board 17 A draws the
        * nav collapsed, so with it open (944 inside the card) People is
        * narrower and truncates rather than the whole table scrolling.
        */}
      <div className="overflow-x-auto rounded-xl border border-line bg-surface">
        <div className="min-w-[900px]">
          <div className={cn(GRID, 'h-11 border-b border-line bg-surface-alt text-[11.5px] font-semibold text-text-muted')}>
            <span>Role</span>
            <HeadCell glyph="calendar">Calendars, tasks, notes</HeadCell>
            <HeadCell glyph="inbox">Inboxes — texts &amp; email</HeadCell>
            <HeadCell glyph="people">Book &amp; reassign</HeadCell>
            <span>People</span>
          </div>
          {rows === null && !error && <div className="px-4 py-3 text-[12.5px] text-text-muted">Loading…</div>}
          {rows?.map((row) => (
            <div key={row.id}
              className={cn(GRID, 'h-[52px] border-b border-line-faint last:border-b-0',
                openRow === row.id && 'bg-green-band-bg')}>
              <b className="truncate pr-3 text-[13px]">{row.name}</b>
              <div>
                <ScopeSelect
                  label={`Calendars, tasks, notes for ${row.name}`}
                  value={row.calendarScope}
                  options={CALENDAR_SCOPE_OPTIONS}
                  disabled={!canEdit}
                  onOpenChange={(open) => setOpenRow(open ? row.id : null)}
                  onChange={(calendarScope) => void save(row, { calendarScope })}
                />
              </div>
              <div>
                <ScopeSelect
                  label={`Inboxes for ${row.name}`}
                  value={row.inboxScope}
                  options={INBOX_SCOPE_OPTIONS}
                  disabled={!canEdit}
                  onOpenChange={(open) => setOpenRow(open ? row.id : null)}
                  onChange={(inboxScope) => void save(row, { inboxScope })}
                />
              </div>
              <div>
                <Switch
                  label={`Book & reassign for ${row.name}`}
                  hideLabel
                  checked={row.bookOthers}
                  disabled={!canEdit}
                  onChange={(bookOthers) => void save(row, { bookOthers })}
                />
              </div>
              <span className="truncate text-[12px] text-text-muted">{rolePeopleWords(row)}</span>
            </div>
          ))}
        </div>
      </div>

      {error && <p role="alert" className="m-0 mt-2 text-[12.5px] text-danger">{error}</p>}

      <div role="note" className={cn('mt-3 flex items-start gap-[9px] rounded-[10px] border border-slate-band-line',
        'bg-slate-band-bg px-3 py-2.5 text-[12.5px] leading-[1.45] text-slate-band-text')}>
        <Glyph name="info" size={15} className="mt-px" />
        <div>
          <b>Only their own</b> means what they’re on, made, or were given — plus any person they’ve been granted
          (on the person’s page). Crew roles have no Inbox.
        </div>
      </div>
    </section>
  )
}
